import { AfterViewInit, Component, ElementRef, Input, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { ComposedRail, StoreDensity, StoreItem } from '../models/store-rail.model';

@Component({
  selector: 'app-store-rail',
  templateUrl: './store-rail.component.html',
  styleUrls: ['../home.component.scss', './store-rail.component.scss'],
  standalone: false,
})
export class StoreRailComponent implements AfterViewInit, OnDestroy {
  @Input() rail!: ComposedRail;
  @Input() last = false;
  @Input() signedIn = false;
  @Input() lockColumns = false;
  @ViewChildren('pitchBox') pitchBoxes!: QueryList<ElementRef<HTMLElement>>;

  flippedId: string | null = null;
  readonly starIndexes = [0, 1, 2, 3, 4];
  private pitchObserver: ResizeObserver | null = null;

  constructor(private router: Router) {}

  ngAfterViewInit(): void {
    this.observePitches();
    this.pitchBoxes.changes.subscribe(() => this.observePitches());
  }

  ngOnDestroy(): void {
    this.pitchObserver?.disconnect();
  }

  showPitchMarquee(density: StoreDensity): boolean {
    return density === 5;
  }

  pitchDuration(desc: string): string {
    return Math.max(10, Math.min(28, (desc || '').length * 0.14)) + 's';
  }

  private observePitches(): void {
    this.pitchObserver?.disconnect();
    if (typeof ResizeObserver === 'undefined') {
      this.markPitchOverflow();
      return;
    }
    this.pitchObserver = new ResizeObserver(() => this.markPitchOverflow());
    this.pitchBoxes?.forEach((ref) => this.pitchObserver?.observe(ref.nativeElement));
    this.markPitchOverflow();
  }

  private markPitchOverflow(): void {
    this.pitchBoxes?.forEach((ref) => {
      const box = ref.nativeElement;
      if (!box.classList.contains('tile-back-desc--marquee')) return;
      const first = box.querySelector('.tile-back-pitch') as HTMLElement | null;
      const text = first?.textContent?.trim() || '';
      const textWidth = this.measurePitchWidth(text, getComputedStyle(box));
      box.classList.toggle('is-scrolling', textWidth > box.clientWidth + 2);
    });
  }

  private measurePitchWidth(text: string, style: CSSStyleDeclaration): number {
    if (typeof document === 'undefined' || !text) return 0;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return text.length * 7;
    ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    return ctx.measureText(text).width;
  }

  isFlipped(id: string): boolean {
    return this.flippedId === id;
  }

  starFill(rating: number, index: number): 'full' | 'half' | 'empty' {
    const filled = Math.round(Math.max(0, Math.min(5, rating)) * 2) / 2;
    if (filled >= index + 1) return 'full';
    if (filled >= index + 0.5) return 'half';
    return 'empty';
  }

  storePrice(item: StoreItem): string {
    return item.price > 0 ? '$' + item.price : 'Free';
  }

  showStorePlayStats(density: StoreDensity): boolean {
    return density === 2 || density === 3 || density === 4 || density === 5;
  }

  showCaptionSystem(density: StoreDensity): boolean {
    return density === 2 || density === 3 || density === 4;
  }

  showSystemChip(density: StoreDensity): boolean {
    return true;
  }

  showReviewCount(density: StoreDensity): boolean {
    return density === 2 || density === 3 || density === 4;
  }

  showHoverType(density: StoreDensity): boolean {
    return density !== 5;
  }

  showHoverPlayStats(_density: StoreDensity): boolean {
    return false;
  }

  showHoverAuthor(density: StoreDensity): boolean {
    return density === 3 || density === 4 || density === 5;
  }

  showHoverDesc(density: StoreDensity): boolean {
    return true;
  }

  storePlaytimeCompact(item: StoreItem): string {
    return item.playtimeHrs + 'h';
  }

  storeCaptionMeta(item: StoreItem): string {
    const parts = [this.storePlaytimeCompact(item)];
    const players = (item.party.split('·')[1] || '').trim();
    if (players) parts.push(players);
    const level = this.storeLevel(item);
    if (level) parts.push(level);
    return parts.join(' · ');
  }

  shortType(type: StoreItem['type']): string {
    if (type === 'One Shot Encounter') return 'One Shot';
    if (type === 'Mini Campaign / Adventure Arc') return 'Mini Campaign';
    return type;
  }

  storePlaytime(item: StoreItem): string {
    return item.playtimeHrs === 1 ? '1 hr' : item.playtimeHrs + ' hrs';
  }

  storeLevel(item: StoreItem): string {
    return (item.party.split('·')[0] || '').trim();
  }

  storePlayers(item: StoreItem): string {
    const size = (item.party.split('·')[1] || '').trim();
    return size ? size + ' ppl' : '';
  }

  ratingLabel(item: StoreItem): string {
    return `${item.rating} out of 5 stars from ${item.reviews} reviews`;
  }

  authorStoreParams(item: StoreItem): Record<string, string> {
    return { author: item.author };
  }

  createCommands(): { path: string[]; query: Record<string, string> } {
    const query: Record<string, string> = {};
    if (this.rail.createType) query['type'] = this.rail.createType;
    if (this.signedIn) {
      return { path: ['/add-edit-content'], query };
    }
    return { path: ['/sign-up'], query };
  }

  trackItem(_i: number, item: StoreItem): string {
    return item.id;
  }

  onStoreTileActivate(item: StoreItem, event: Event): void {
    if (this.eventFromNestedLink(event)) return;
    const id = this.rail.id + ':' + item.id;
    if (this.isCoarsePointer()) {
      if (this.flippedId !== id) {
        event.preventDefault();
        this.flippedId = id;
        return;
      }
    }
    this.router.navigate(['/store'], { queryParams: { ...this.rail.seeAllParams, id: item.id } });
  }

  onStoreTileKey(item: StoreItem, event: KeyboardEvent): void {
    if (this.eventFromNestedLink(event)) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onStoreTileActivate(item, event);
    }
  }

  private eventFromNestedLink(event: Event): boolean {
    const node = event.target as Node | null;
    const el = node instanceof Element ? node : node?.parentElement;
    return !!el?.closest('a');
  }

  private isCoarsePointer(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;
  }
}
